// src/pages/agents.ts —— 06 C-09 代理管理（0045：去返佣费率、售价表与代改售价）、C-10 分销用户、C-11 佣金结算（含负余额挂账）、C-12 提佣审核。
// 列表 GET /api/admin/agents | agent-users | commissions | commissions/negative-beneficiaries | agent-withdrawals；写端点按 02 §6.2。
// 代理层级由上级推导（D20），编辑不可改上级与登录账号；多级代理（用户 2026-10-18）：层级上限 = 系统参数 maxLevel（默认 10，可调 2～100），
// 列表只留「全部」页签 + 层级 facet，上级候选按关键字远程搜索，树经抽屉逐级下钻；提佣状态机与提币对称（D9 / B-6）
import { ApiError } from '../../../shared/src/api/client';
import type { RouteKey } from '../../../shared/src/api/routes';
import type { AdminPage, AdminAgentPricesView } from '../../../shared/src/api/types';
import type { Dict } from '../../../shared/src/dc';
import { interpolate } from '../../../shared/src/errors';
import { commissionRateText, fmtAmount, fmtCommission, fmtPlain, parseDecimalInput } from '../../../shared/src/money';
import { checkDrafts, draftsOf, fmtDepositRate, fmtOpenPrice, priceBelowItems, pricesPatch, trimDecimal, type PriceDraftError, type PriceDrafts } from '../../../shared/src/prices';
import { fmtDate, fmtDateTime } from '../../../shared/src/time';
import { closeDrawer, openConfirm, openDrawer, openForm, reloadCurrent, sessionGuard } from '../domains/table';
import { str, strArr, type AdminCtx } from '../domains/types';
import { I18N_AGENTS, REWRITES_AGENTS } from '../i18n/agents';
import type { AgentDetail, AgentRow, AgentsSummary, AgentUserRow, AgentWdRow, AgentWdSummary, CommissionRow, CommissionsSummary, NegativeRow, SettleResult } from './agents.types';
import { slaChip } from './funds';
import { openUser360 } from './shells';
import { chipMap, defineView, type ActionDef, type DrawerAction, type DrawerSpec, type ErasedView, type FieldDef, type FormChange, type FormDef, type FormVals, type Need, type PageMap, type StatVals } from './types';
import { cell, chip, chipOf, localErr, withBadge, type CellVals, type ChipKind } from './ui';

const AG_EDIT: Need = { module: 'agents', bit: 'edit' };
const COM_APPROVE: Need = { module: 'commissions', bit: 'approve' };
const AW_APPROVE: Need = { module: 'agentWd', bit: 'approve' };
const PWD_RE = /^(?=.*[A-Za-z])(?=.*\d).{8,64}$/; // 07 §3
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LOGIN_RE = /^[a-z0-9_]{3,32}$/;
const TRON_HASH_RE = /^[0-9a-fA-F]{64}$/;
const NOTE_MAX = 255;
const PARENT_PAGE = 50; // 上级候选：parent_candidate=1 首页 50 条 + 关键字远程搜索（size 白名单 10 / 20 / 50）
const DETAIL_CHILDREN_CAP = 50; // 详情 children 上限（后端 AdminAgentChildren LIMIT 50）
const PWD_PH = '8-20 位，含字母与数字'; // 原型 L1071 / L1099 字面 placeholder

const tz = (ctx: AdminCtx): string => ctx.s.sh.timezone;

// D8-12 截断展示；后端值非法时原样显示（不抛错）
function money(v: string, dp = 2): string {
  try {
    return fmtAmount(v, dp);
  } catch {
    return v;
  }
}
const usd = (v: string): string => (v.trim().startsWith('-') ? `−$${money(v.trim().slice(1))}` : `$${money(v)}`);
// 佣金口径（对客评审 2026-10-15 #26：钱包余额与佣金为 USDT，卡相关为 $）：单值「{金额} USDT」、表格单元格只写去尾零金额（表头带 (USDT)）；非法值原样
const usdt = (v: string): string => fmtCommission(v, { fallback: v });
function plain(v: string): string {
  try {
    return fmtPlain(v);
  } catch {
    return v;
  }
}
// 十进制串去尾随 0（"10.00" → "10"），用于费率百分数展示（原型 `${topRate}%`）
function trimDec(v: string): string {
  const n = parseDecimalInput(v, 6);
  if (n === null) return v;
  return n.includes('.') ? n.replace(/\.?0+$/, '') : n;
}

function short(v: string | null): string {
  if (!v) return '—';
  return v.length > 12 ? `${v.slice(0, 4)}…${v.slice(-4)}` : v;
}

// 层级色：L1 绿 / L2 蓝 / L3 紫，L4 及以下兜底灰（多级代理）；入参为代理 level 数值或佣金 level_label「L{n}」，非法值原样灰显
const LEVEL_KIND: Readonly<Record<number, ChipKind>> = { 1: 'green', 2: 'blue', 3: 'purple' };
function levelChip(l: number | string): CellVals {
  const n = typeof l === 'number' ? l : /^L(\d{1,3})$/.test(l) ? Number(l.slice(1)) : NaN;
  if (!Number.isInteger(n) || n < 1) return chip('gray', String(l));
  return chip(LEVEL_KIND[n] ?? 'gray', `L${n}`);
}

// 06 C-09：sub = @{telegram_username}，无则 {telegram_id}，均空 —（M-05）
function tgOf(a: Pick<AgentRow, 'telegram_username' | 'telegram_id'>): string {
  const u = a.telegram_username?.trim();
  if (u) return u.startsWith('@') ? u : `@${u}`;
  return a.telegram_id === null ? '—' : String(a.telegram_id);
}

function summaryOf<T extends object>(page: Readonly<Record<string, unknown>>, probe: keyof T): T | null {
  const s = page['summary'];
  return typeof s === 'object' && s !== null && probe in s ? (s as T) : null;
}

// facet 选项文案（P-12）：未知值显示原值
const loginLabel = (t: Dict, v: string): string => (v === 'active' ? str(t, 'stOn') || v : v === 'disabled' ? str(t, 'stOff') || v : v);
const settleLabel = (t: Dict, v: string): string => (v === 'settled' ? str(t, 'stSettled') || v : v === 'unsettled' ? str(t, 'stUnsettled') || v : v);
// 06 C-11 类型列：kind → ck + PascalCase(kind)（open → ckOpen、topup → ckTopup）；未登记显示原值。
// 0045：代理受益行为加价差价（开卡差价 / 充值差价 / 汇率差）；受益人为用户的行是用户链拉新奖励（ck{Kind}User：开卡奖励 / 充值奖励）
const kindKey = (kind: string): string => `ck${kind.split('_').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('')}`;
const typeLabel = (t: Dict, v: string): string => str(t, kindKey(v)) || v;
const rowTypeLabel = (t: Dict, r: CommissionRow): string => (r.beneficiary.kind === 'user' ? str(t, `${kindKey(r.kind)}User`) || typeLabel(t, r.kind) : typeLabel(t, r.kind));

// 06 C-12 状态集（S-50 / S-65 / X-34）
const WD_STATUS: Readonly<Record<string, readonly [ChipKind, string]>> = {
  pending: ['amber', 'stPending'], approved: ['blue', 'stApproved'], paying: ['blue', 'stPaying'], paid: ['green', 'stPaid'],
  failed: ['red', 'stVerifyFailed'], rejected: ['red', 'stReject'],
};

// ---- 代理（C-09） ----

interface ParentOption {
  label: string;
  id: number | null;
  level: number;
}

const agentLabel = (a: { biz_no: string; name: string }): string => `${a.biz_no} · ${a.name}`;

interface ParentCandidates {
  options: ParentOption[];
  maxLevel: number | null; // summary.max_level（当前 maxLevel 配置；缺省 null）
}

// 06 C-09（多级代理 2026-10-18）：上级候选 = 在册且 level < 当前 maxLevel 的代理（后端 parent_candidate=1 过滤，超限建号仍由后端 1305 兜底）；
// keyword 为空取首页，非空按编号 / 名称 / 账号 / 邮箱 / 推荐码远程搜索。首项「平台直属」= 不带 parent_agent_id
async function parentCandidates(ctx: AdminCtx, keyword: string): Promise<ParentCandidates> {
  const kw = keyword.trim();
  const page = await ctx.gp.api.call<AdminPage<AgentRow> & { summary?: Partial<AgentsSummary> }>('GET /api/admin/agents', {
    query: { parent_candidate: '1', page: 1, size: PARENT_PAGE, ...(kw ? { keyword: kw } : {}) },
  });
  const rows = Array.isArray(page.list) ? page.list : [];
  const max = page.summary?.max_level;
  return {
    options: [{ label: str(ctx.t, 'platformDirect'), id: null, level: 0 }, ...rows.map((a) => ({ label: agentLabel(a), id: a.id, level: a.level }))],
    maxLevel: typeof max === 'number' && Number.isInteger(max) ? max : null,
  };
}

// 06 C-09 表单表逐行：编辑态登录账号 / 上级只读、无密码字段、追加绑定用户 UID；推荐码与层级恒只读；0045 无费率字段（售价在抽屉「售价」代改）
function agentFields(t: Dict, editing: boolean, parents: readonly ParentOption[]): readonly FieldDef[] {
  return [
    { k: 'name', label: str(t, 'cAgentName'), type: 'text' },
    { k: 'login_account', label: str(t, 'cAgentAcct'), type: editing ? 'readonly' : 'text', placeholder: 'agent_kevin' },
    { k: 'email', label: str(t, 'cEmail2'), type: 'text', placeholder: 'name@mail.com' },
    ...(editing ? [] : [{ k: 'password', label: str(t, 'cInitPwd'), type: 'password' as const, wide: true, placeholder: PWD_PH }]),
    { k: 'telegram_id', label: str(t, 'cTgId'), type: 'text', optional: true, placeholder: '7100000001' },
    { k: 'telegram_username', label: str(t, 'cTgName'), type: 'text', optional: true, placeholder: '@username' },
    { k: 'code', label: str(t, 'cCode'), type: 'readonly' },
    { k: 'level', label: str(t, 'cLevel'), type: 'readonly' },
    ...(editing ? [] : [{ k: 'parent_kw', label: str(t, 'cParentSearch'), type: 'text' as const, optional: true, placeholder: str(t, 'parentSearchPh') }]),
    editing ? { k: 'parent', label: str(t, 'cUpline'), type: 'readonly' } : { k: 'parent', label: str(t, 'cUpline'), type: 'select', options: parents.map((p) => p.label) },
    ...(editing ? [{ k: 'bound_user_uid', label: str(t, 'cBoundUser'), type: 'text' as const, optional: true, wide: true }] : []),
  ];
}

// 编辑态 init：TG 两字段只在值变更时校验（存量非 @ 开头的用户名不阻塞其他字段保存）；telegram_id 后端为指针语义（null = 不改），无清空通道 → 由有值改空本地拦截
function validateAgent(t: Dict, v: FormVals, editing: boolean, init?: FormVals): string {
  const name = String(v['name'] ?? '').trim();
  if (name.length < 1 || [...name].length > 64) return str(t, 'errAgentName');
  if (!editing && !LOGIN_RE.test(String(v['login_account'] ?? '').trim())) return str(t, 'errLoginAccount');
  if (!EMAIL_RE.test(String(v['email'] ?? '').trim())) return str(t, 'errEmail');
  if (!editing && !PWD_RE.test(String(v['password'] ?? ''))) return str(t, 'errPwdRule');
  const tg = String(v['telegram_id'] ?? '').trim();
  const tgInit = String(init?.['telegram_id'] ?? '');
  if (editing && tg === '' && tgInit !== '') return str(t, 'fndErrTgIdClear');
  if (tg && tg !== tgInit && !(/^\d{1,20}$/.test(tg) && Number.isSafeInteger(Number(tg)))) return str(t, 'errTelegramId');
  const tgu = String(v['telegram_username'] ?? '').trim();
  if (tgu && tgu !== String(init?.['telegram_username'] ?? '') && !/^@\w{1,64}$/.test(tgu)) return str(t, 'fndErrTgUser');
  return '';
}

const formTitle = (t: Dict, editing: boolean): string => `${str(t, editing ? 'edit' : 'add')} · ${str(t, 'agents')}`;

// 由所选上级推导新代理层级（只读展示；提交后以响应 level 为准）
const levelPreview = (t: Dict, p: ParentOption | undefined): string => (p ? `L${p.level + 1}` : str(t, 'levelAuto'));

async function openAgentCreate(ctx: AdminCtx): Promise<void> {
  const t = ctx.t;
  const alive = sessionGuard(ctx);
  const first = await parentCandidates(ctx, '');
  if (!alive()) return;
  const route: RouteKey = 'POST /api/admin/agents';
  // 已见过的候选（首页 + 各次搜索结果），按 label 取回 id；label 含 biz_no，唯一
  const known = new Map<string, ParentOption>(first.options.map((p) => [p.label, p]));
  const platform = first.options[0];
  openForm(ctx, {
    title: formTitle(t, false),
    // 06 C-09：新增 tip agentTip；层级只读位灰字 levelAutoTip（由上级推导，上限 = 当前 maxLevel；提交后以响应 level 为准）
    tip: `${str(t, 'agentTip')} ${interpolate(str(t, 'levelAutoTip'), { max: first.maxLevel ?? '—' })}`,
    fields: agentFields(t, false, first.options),
    init: {
      name: '', login_account: '', email: '', password: '', telegram_id: '', telegram_username: '', code: '—', parent_kw: '',
      level: levelPreview(t, platform), parent: platform?.label ?? '',
    },
    validate: (v) => validateAgent(t, v, false),
    onChange: async (c, k, vals): Promise<FormChange> => {
      if (k === 'parent') return { vals: { level: levelPreview(t, known.get(String(vals['parent'] ?? ''))) } };
      if (k !== 'parent_kw') return {};
      const found = await parentCandidates(c, String(vals['parent_kw'] ?? ''));
      for (const p of found.options) known.set(p.label, p);
      const hits = found.options.length - 1;
      const pick = found.options[hits > 0 ? 1 : 0];
      return {
        options: { parent: found.options.map((p) => p.label) },
        vals: { parent: pick?.label ?? '', level: levelPreview(t, pick) },
        err: hits > 0 ? '' : str(t, 'parentSearchNone'),
      };
    },
    submit: async (c, v) => {
      const parent = known.get(String(v['parent'] ?? ''));
      const tg = String(v['telegram_id'] ?? '').trim();
      const tgu = String(v['telegram_username'] ?? '').trim();
      const done = sessionGuard(c);
      await c.gp.api.call<{ id: number; biz_no: string; code: string; level: number }>(route, {
        body: {
          // 0045：不带 open_rate / spend_rate（后端严格解码 4220）；新代理售价全部继承上级
          name: String(v['name']).trim(), login_account: String(v['login_account']).trim(), email: String(v['email']).trim(), password: String(v['password']),
          ...(tg ? { telegram_id: Number(tg) } : {}), ...(tgu ? { telegram_username: tgu } : {}), ...(parent && parent.id !== null ? { parent_agent_id: parent.id } : {}),
        },
      });
      if (!done()) return;
      c.gp.ui.toast(str(t, 'toastAgentCreated'));
      void reloadCurrent(c);
    },
  });
}

// 06 C-09 编辑：先取详情；只提交变更字段；bound_user_uid 三态——未动不带键、清空发 null、填写发字符串（空串后端 4220）；
// 不带 login_account / parent_agent_id / password（严格解码 4220，SP6 TD-18 / G-12）；无改动不发请求直接关闭
async function openAgentEdit(ctx: AdminCtx, row: AgentRow): Promise<void> {
  const t = ctx.t;
  const alive = sessionGuard(ctx);
  const d = await ctx.gp.api.call<AgentDetail>('GET /api/admin/agents/{id}', { path: { id: row.id } });
  if (!alive()) return;
  const route: RouteKey = 'PUT /api/admin/agents/{id}';
  const parentLabel = d.parent ? agentLabel(d.parent) : str(t, 'platformDirect');
  const init: FormVals = {
    name: d.name, login_account: d.login_account, email: d.email, telegram_id: d.telegram_id === null ? '' : String(d.telegram_id),
    telegram_username: d.telegram_username ?? '', code: d.code, level: `L${d.level}`, parent: parentLabel,
    bound_user_uid: d.bound_user?.uid ?? '',
  };
  openForm(ctx, {
    title: formTitle(t, true),
    tip: `${str(t, 'agentEditTip')} ${str(t, 'bindUserTip')}`,
    fields: agentFields(t, true, []),
    init,
    validate: (v) => validateAgent(t, v, true, init),
    submit: async (c, v) => {
      const patch: Record<string, string | number | null> = {};
      const s = (k: string): string => String(v[k] ?? '').trim();
      if (s('name') !== d.name) patch['name'] = s('name');
      if (s('email') !== d.email) patch['email'] = s('email');
      // 后端 tree.go 指针语义：null = 不改；用户名清空发 ''；telegram_id 清空已由 validate 拦截（无后端通道）
      if (s('telegram_id') && s('telegram_id') !== String(init['telegram_id'])) patch['telegram_id'] = Number(s('telegram_id'));
      if (s('telegram_username') !== String(init['telegram_username'])) patch['telegram_username'] = s('telegram_username');
      if (s('bound_user_uid') !== String(init['bound_user_uid'])) patch['bound_user_uid'] = s('bound_user_uid') || null;
      if (Object.keys(patch).length === 0) {
        c.gp.ui.toast(str(t, 'toastSaved')); // 无改动：不发请求，直接关闭（同 C-13 产品编辑口径）
        return;
      }
      const done = sessionGuard(c);
      try {
        await c.gp.api.call(route, { path: { id: d.id }, body: patch });
      } catch (e) {
        // 绑定用户不存在 / 已绑定其他代理：4220 bound_user_uid 或 UNIQUE 冲突（06 C-09 行内红条 errBindUser）
        if (e instanceof ApiError && e.code === 4220 && e.data.field === 'bound_user_uid') throw localErr(route, str(t, 'errBindUser'));
        throw e;
      }
      if (!done()) return;
      c.gp.ui.toast(str(t, 'toastSaved'));
      void reloadCurrent(c);
    },
  });
}

function resetPwdForm(t: Dict, a: Pick<AgentRow, 'id' | 'name' | 'email'>): FormDef {
  return {
    title: `${str(t, 'aResetAgentPwd')} · ${a.name}`,
    tip: `${str(t, 'adminPwdTip')} ${a.email || '—'}`,
    fields: [{ k: 'password', label: str(t, 'cInitPwd'), type: 'password', wide: true, placeholder: PWD_PH }],
    init: { password: '' },
    validate: (v) => (PWD_RE.test(String(v['password'] ?? '')) ? '' : str(t, 'errPwdRule')),
    submit: async (c, v) => {
      const alive = sessionGuard(c);
      await c.gp.api.call('POST /api/admin/agents/{id}/reset-password', { path: { id: a.id }, body: { password: String(v['password']) } });
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastAgentPwd'));
    },
  };
}

// settle-all / 单笔 settle 统一返回 SettleResult：失败受益人数 > 0 时追加「{n} 笔失败」
function settleText(t: Dict, base: string, r: SettleResult): string {
  return r.failed > 0 ? `${base} · ${str(t, 'settleFailedN').replace('{n}', String(r.failed))}` : base;
}
const batchText = (t: Dict, r: SettleResult): string => settleText(t, str(t, 'toastSettleDone').replace('{n}', String(r.beneficiaries)).replace('{settled}', usdt(r.settled)), r);

// 批量结算：agent 为空 = 全量（C-11 主操作，body 不带 agent_id）；否则 = 该代理全部 unsettled（C-09 行内，A-10）
function settleAllConfirm(ctx: AdminCtx, agent: AgentRow | null): void {
  const t = ctx.t;
  openConfirm(ctx, {
    title: str(t, 'settleTitle'),
    text: agent ? str(t, 'settleAgentText').replace('{agent}', agent.name) : str(t, 'settleAllText'),
    label: agent ? agentLabel(agent) : str(t, 'aSettle'),
    run: async (c) => {
      const alive = sessionGuard(c);
      const r = await c.gp.api.call<SettleResult>('POST /api/admin/commissions/settle-all', { body: agent ? { agent_id: agent.id } : {} });
      if (!alive()) return;
      c.gp.ui.toast(batchText(t, r));
      await reloadCurrent(c);
    },
  });
}

// 启停登录：文案按响应 status（停用仅禁登录，计佣与 Bot 照常，D20）
async function toggleLogin(ctx: AdminCtx, a: AgentRow): Promise<void> {
  const alive = sessionGuard(ctx);
  try {
    const r = await ctx.gp.api.call<{ status: string }>('POST /api/admin/agents/{id}/toggle-login', { path: { id: a.id }, body: {} });
    if (!alive()) return;
    ctx.gp.ui.toast(str(ctx.t, r.status === 'disabled' ? 'toastAgentLoginOff' : 'toastAgentLoginOn'));
    closeDrawer(ctx);
    await reloadCurrent(ctx);
  } catch (e) {
    if (alive()) ctx.gp.ui.toast(ctx.gp.ui.err(e));
  }
}

function deleteConfirm(ctx: AdminCtx, a: AgentRow): void {
  const t = ctx.t;
  openConfirm(ctx, {
    title: str(t, 'delTitle'),
    text: str(t, 'delAgentText'),
    label: a.name,
    // 1304（佣金余额 / 未结算 / 在途提佣）由 doConfirm 以 ui.err toast
    run: async (c) => {
      const alive = sessionGuard(c);
      await c.gp.api.call('DELETE /api/admin/agents/{id}', { path: { id: a.id } });
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastDeleted'));
      closeDrawer(c);
      await reloadCurrent(c);
    },
  });
}

function agentDrawer(ctx: AdminCtx, a: AgentRow, d: AgentDetail | null): DrawerSpec {
  const t = ctx.t;
  const x: AgentRow = d ?? a;
  const upline = x.parent ? agentLabel(x.parent) : str(t, 'platformDirect');
  const chain = d ? ['GoodPayU', ...d.upline_chain.map((u) => `${u.name} (${u.biz_no})`), `${d.name} (${d.biz_no})`].join(' → ') : '—';
  const bound = d?.bound_user ? d.bound_user.uid : str(t, 'tgUnbound');
  return {
    kind: str(t, 'agents'),
    title: `${x.name} · ${x.biz_no}`,
    subtitle: `${tgOf(x)} · L${x.level} · ${str(t, 'cUpline')} ${upline}`,
    fields: [
      { k: str(t, 'cAgentAcct'), v: x.login_account },
      { k: str(t, 'cEmail2'), v: x.email },
      { k: str(t, 'cLoginStatus'), v: `${str(t, x.status === 'disabled' ? 'stOff' : 'stOn')} · ${d?.last_login_at ? fmtDateTime(d.last_login_at, { tz: tz(ctx) }) : '—'}` },
      { k: str(t, 'cCode'), v: x.code },
      { k: str(t, 'cLevel'), v: `L${x.level}` },
      { k: str(t, 'cInvites'), v: `${x.direct_users} / ${x.team_users}` },
      { k: str(t, 'cEarned'), v: d ? usdt(d.earned_total) : '—' },
      { k: str(t, 'cUnsettled'), v: d ? usdt(d.unsettled_total) : '—' },
      { k: str(t, 'uCommBal'), v: d ? usdt(d.commission_balance) : '—' },
      { k: str(t, 'cBoundUser'), v: d ? bound : '—' },
      { k: str(t, 'agentTree'), v: chain, wide: true },
      { k: str(t, 'cRefLink'), v: d?.links.bot ?? '—', wide: true },
      { k: `${str(t, 'cRefLink')} (Web)`, v: d?.links.web ?? '—', wide: true },
    ],
    list: {
      title: `${str(t, 'downline')} / ${str(t, 'myUsers')}`,
      rows: d
        ? [
            // 多级代理：点击直接下级下钻到其抽屉（逐级按需展开整棵树）
            ...d.children.map((k) => ({ left: `▸ ${k.name} · ${k.biz_no}`, sub: `L${k.level} · ${k.code}`, right: usdt(k.earned), rightSub: str(t, 'cEarned'), tone: 'good' as const, go: (c: AdminCtx) => openAgentByID(c, k.id) })),
            ...(d.children.length >= DETAIL_CHILDREN_CAP ? [{ left: interpolate(str(t, 'downlineMore'), { n: DETAIL_CHILDREN_CAP }), sub: '', right: '', rightSub: '', tone: '' as const }] : []),
            ...d.users.map((u) => ({ left: u.masked_email ?? u.uid, sub: u.uid, right: usd(u.spent_total), rightSub: str(t, 'cSpend'), tone: '' as const, go: (c: AdminCtx) => openUser360(c, u.id) })),
          ]
        : [],
    },
    actions: [
      { label: str(t, 'fxHistory'), kind: '', run: (c) => void openAgentFXHistory(c, x) },
      { label: str(t, 'agentPricesTitle'), kind: '', run: (c) => void openAgentPrices(c, x) },
      { label: str(t, 'aResetAgentPwd'), kind: '', need: AG_EDIT, run: (c) => openForm(c, resetPwdForm(t, x)) },
      { label: str(t, x.status === 'disabled' ? 'aEnableLogin' : 'aDisableLogin'), kind: x.status === 'disabled' ? 'green' : 'danger', need: AG_EDIT, run: (c) => toggleLogin(c, x) },
      { label: str(t, 'aEdit'), kind: 'primary', need: AG_EDIT, run: (c) => openAgentEdit(c, x) },
    ],
  };
}

function openAgentFXHistory(ctx: AdminCtx, a: AgentRow): void {
  const alive = sessionGuard(ctx);
  const prior = ctx.host.state.tb.drawer;
  void ctx.gp.api.call<{ list: Array<{ id: number; rate: string | null; prev_rate: string | null; base_rate_snap: string; limit_pct_snap: string; effective_at: string }>; total: number }>(
    'GET /api/admin/agents/{id}/fx-rates', { path: { id: a.id }, query: { page: 1, size: 20 } },
  ).then((history) => {
    if (!alive() || ctx.host.state.tb.drawer !== prior) return;
    openDrawer(ctx, {
      kind: str(ctx.t, 'fxHistory'), title: `${a.name} · ${a.biz_no}`, subtitle: str(ctx.t, 'fxHistoryReadOnly'),
      fields: history.list.map((r) => ({ k: fmtDateTime(r.effective_at, { tz: tz(ctx) }), v: r.rate ?? str(ctx.t, 'fxInherited'), wide: true })),
      note: history.total > history.list.length,
      actions: [{ label: str(ctx.t, 'aClose'), kind: 'primary', run: (c) => closeDrawer(c) }],
    });
  }).catch((e: unknown) => { if (alive() && ctx.host.state.tb.drawer === prior) ctx.gp.ui.toast(ctx.gp.ui.err(e)); });
}

// ---- 代理售价（C-09 抽屉「售价」，0045 代理加价定价） ----

const productName = (ctx: AdminCtx, r: { name: string; name_en: string | null }): string => (ctx.lang === 'en' && r.name_en ? r.name_en : r.name);

function pricesUpline(t: Dict, v: AdminAgentPricesView): string {
  return v.parent ? interpolate(str(t, 'pricesUplineAgent'), { name: v.parent.name, biz_no: v.parent.biz_no }) : str(t, 'pricesUplinePlatform');
}

// 售价表抽屉：每个上架产品一行 + 末行充值费率；below_parent → 右下红字（tone bad）；代改需 agents.edit
function pricesDrawer(ctx: AdminCtx, a: AgentRow, v: AdminAgentPricesView): DrawerSpec {
  const t = ctx.t;
  const own = (x: string | null, fmt: (s: string) => string): string => (x === null ? str(t, 'pricesInherit') : fmt(x));
  const tail = (below: boolean, earnKey: string, earn: string) => ({ rightSub: below ? str(t, 'priceBelowParentTip') : `${str(t, earnKey)} ${earn}`, tone: below ? ('bad' as const) : ('good' as const) });
  const d = v.deposit;
  return {
    kind: str(t, 'agentPricesTitle'),
    title: `${a.name} · ${a.biz_no}`,
    subtitle: `${pricesUpline(t, v)} · ${str(t, 'pricesTip')}`,
    list: {
      title: str(t, 'agentPricesTitle'),
      rows: [
        ...v.open.map((r) => ({
          left: `${productName(ctx, r)} · ${r.bin}`,
          sub: `${str(t, 'pricesParent')} ${fmtOpenPrice(r.parent_price)} · ${str(t, 'pricesOwn')} ${own(r.own_price, fmtOpenPrice)}`,
          right: fmtOpenPrice(r.effective_price),
          ...tail(r.below_parent, 'pricesEarn', fmtOpenPrice(r.earn)),
        })),
        {
          left: str(t, 'pricesDeposit'),
          sub: `${str(t, 'pricesParent')} ${fmtDepositRate(d.parent_rate)} · ${str(t, 'pricesOwn')} ${own(d.own_rate, fmtDepositRate)}`,
          right: fmtDepositRate(d.effective_rate),
          ...tail(d.below_parent, 'pricesEarnRate', fmtDepositRate(d.earn)),
        },
      ],
    },
    actions: [
      { label: str(t, 'aClose'), kind: '', run: (c) => closeDrawer(c) },
      { label: str(t, 'aEditPrices'), kind: 'primary', need: AG_EDIT, run: (c) => openForm(c, pricesForm(c, a, v)) },
    ],
  };
}

const openKey = (productId: number): string => `open_${productId}`;

function draftsFromForm(v: AdminAgentPricesView, vals: FormVals): PriceDrafts {
  const open: Record<number, string> = {};
  for (const r of v.open) open[r.product_id] = String(vals[openKey(r.product_id)] ?? '');
  return { open, deposit: String(vals['deposit'] ?? '') };
}

function draftErr(t: Dict, e: PriceDraftError, label: string, parent: string, fmtKey: 'errPriceFormatOpen' | 'errPriceFormatRate'): string {
  if (e === 'format') return interpolate(str(t, fmtKey), { label });
  if (e === 'below') return interpolate(str(t, 'errPriceBelow'), { label, parent });
  return '';
}

// 代改售价表单：每个上架产品一个开卡价输入（留空 = 继承）+ 充值费率；只提交改动项；1310 逐项列出「不能低于上级价」
function pricesForm(ctx: AdminCtx, a: AgentRow, v: AdminAgentPricesView): FormDef {
  const t = ctx.t;
  const route: RouteKey = 'PUT /api/admin/agents/{id}/prices';
  const init = draftsOf(v);
  const openLabel = (r: AdminAgentPricesView['open'][number]): string => interpolate(str(t, 'pricesFieldOpen'), { name: productName(ctx, r), bin: r.bin, parent: fmtOpenPrice(r.parent_price) });
  const depositLabel = interpolate(str(t, 'pricesFieldDeposit'), { parent: fmtDepositRate(v.deposit.parent_rate) });
  const firstError = (vals: FormVals): string => {
    const chk = checkDrafts(v, draftsFromForm(v, vals));
    for (const r of v.open) {
      const e = draftErr(t, chk.open[r.product_id] ?? '', openLabel(r), fmtOpenPrice(r.parent_price), 'errPriceFormatOpen');
      if (e) return e;
    }
    return draftErr(t, chk.deposit, depositLabel, fmtDepositRate(v.deposit.parent_rate), 'errPriceFormatRate');
  };
  return {
    title: `${str(t, 'aEditPrices')} · ${a.name}`,
    tip: `${pricesUpline(t, v)}。${str(t, 'pricesFormTip')}`,
    fields: [
      ...v.open.map((r) => ({ k: openKey(r.product_id), label: openLabel(r), type: 'text' as const, optional: true, placeholder: trimDecimal(r.parent_price, 2) })),
      { k: 'deposit', label: depositLabel, type: 'text', optional: true, placeholder: trimDecimal(v.deposit.parent_rate, 4), wide: true },
    ],
    init: { ...Object.fromEntries(v.open.map((r) => [openKey(r.product_id), init.open[r.product_id] ?? ''])), deposit: init.deposit },
    validate: (vals) => firstError(vals),
    submit: async (c, vals) => {
      const body = pricesPatch(v, draftsFromForm(v, vals));
      if (!body) {
        c.gp.ui.toast(str(t, 'pricesNoChange')); // 无改动：不发请求，直接关闭
        return;
      }
      const alive = sessionGuard(c);
      let next: AdminAgentPricesView;
      try {
        next = await c.gp.api.call<AdminAgentPricesView>(route, { path: { id: a.id }, body });
      } catch (e) {
        const items = priceBelowItems(e);
        if (items.length > 0) {
          // 1310：逐项列出（开卡按 product_id 对应产品名，充值单列）；表单不关，供改正后重提
          const lines = items.map((it) => {
            const r = v.open.find((x) => x.product_id === it.product_id);
            return it.kind === 'deposit' || !r
              ? interpolate(str(t, 'errPriceBelow'), { label: depositLabel, parent: fmtDepositRate(it.parent_eff) })
              : interpolate(str(t, 'errPriceBelow'), { label: openLabel(r), parent: fmtOpenPrice(it.parent_eff) });
          });
          throw localErr(route, lines.join('；'));
        }
        throw e;
      }
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastPricesSaved'));
      openDrawer(c, pricesDrawer(c, a, next));
    },
  };
}

// 抽屉「售价」：取 GET /api/admin/agents/{id}/prices 后换成售价表抽屉。会话已换、抽屉已关或已换成别的代理则丢弃；
// 同一代理的行抽屉被详情回填替换（标题相同）不算「已换」，避免详情未到时点击被静默吞掉
function openAgentPrices(ctx: AdminCtx, a: AgentRow): Promise<void> {
  const alive = sessionGuard(ctx);
  const title = `${a.name} · ${a.biz_no}`;
  const same = (): boolean => alive() && ctx.host.state.tb.drawer?.title === title;
  return ctx.gp.api
    .call<AdminAgentPricesView>('GET /api/admin/agents/{id}/prices', { path: { id: a.id } })
    .then((v) => {
      if (same()) openDrawer(ctx, pricesDrawer(ctx, a, v));
    })
    .catch((e: unknown) => {
      if (same()) ctx.gp.ui.toast(ctx.gp.ui.err(e));
    });
}

// 行点击：先以列表行开抽屉，再取详情回填（会话已换、或当前抽屉已不是本次打开的那一个则丢弃）
function openAgent(ctx: AdminCtx, a: AgentRow): void {
  const spec = agentDrawer(ctx, a, null);
  openDrawer(ctx, spec);
  const alive = sessionGuard(ctx);
  void ctx.gp.api
    .call<AgentDetail>('GET /api/admin/agents/{id}', { path: { id: a.id } })
    .then((d) => {
      if (!alive() || ctx.host.state.tb.drawer !== spec) return;
      openDrawer(ctx, agentDrawer(ctx, a, d));
    })
    .catch((e: unknown) => {
      if (alive() && ctx.host.state.tb.drawer === spec) ctx.gp.ui.toast(ctx.gp.ui.err(e));
    });
}

// 抽屉下钻：按 id 取详情后直接打开（详情即 AgentRow 超集）
function openAgentByID(ctx: AdminCtx, id: number): void {
  const alive = sessionGuard(ctx);
  void ctx.gp.api
    .call<AgentDetail>('GET /api/admin/agents/{id}', { path: { id } })
    .then((d) => {
      if (alive()) openDrawer(ctx, agentDrawer(ctx, d, d));
    })
    .catch((e: unknown) => {
      if (alive()) ctx.gp.ui.toast(ctx.gp.ui.err(e));
    });
}

const levelLabel = (_t: Dict, v: string): string => `L${v}`;

// 多级代理（用户 2026-10-18）：只留「全部」页签；层级筛选为 level facet（选项 1..max(maxLevel, 最深在册层级) 由服务端 facet_options 下发）；
// sort 仅 created_at（last_login_at 未实现，J-10）
function agentsView(key: 'all'): ErasedView {
  return defineView<AgentRow>({
    key,
    source: { route: 'GET /api/admin/agents', tabParam: 'tab' },
    echo: '/admin/agents',
    cols: [
      { label: 'cAgentName' }, { label: 'cAgentAcct' }, { label: 'cUpline' }, { label: 'cLevel', facet: 'level', facetLabel: levelLabel }, { label: 'cCode' }, { label: 'cInvites' },
      { label: 'cJoined', sortKey: 'created_at' }, { label: 'cLoginStatus', facet: 'login_status', facetLabel: loginLabel },
    ],
    cells: (c, a) => [
      cell(`${a.name} · ${a.biz_no}`, tgOf(a)),
      cell(a.login_account, a.email, true),
      cell(a.parent ? agentLabel(a.parent) : str(c.t, 'platformDirect')),
      levelChip(a.level),
      cell(a.code, '', true),
      cell(`${a.direct_users} / ${a.team_users}`),
      cell(fmtDateTime(a.created_at, { tz: tz(c) })),
      a.status === 'disabled' ? chip('red', str(c.t, 'stOff')) : chip('green', str(c.t, 'stOn')),
    ],
    open: openAgent,
    rowActions: (c): readonly ActionDef<AgentRow>[] => [
      { label: 'aEdit', need: AG_EDIT, run: (cc, a) => openAgentEdit(cc, a) },
      { label: 'aResetAgentPwd', need: AG_EDIT, run: (cc, a) => openForm(cc, resetPwdForm(c.t, a)) },
      { label: 'aDisableLogin', need: AG_EDIT, danger: true, show: (a) => a.status !== 'disabled', run: toggleLogin },
      { label: 'aEnableLogin', need: AG_EDIT, show: (a) => a.status === 'disabled', run: toggleLogin },
      { label: 'aSettle', need: COM_APPROVE, run: (cc, a) => settleAllConfirm(cc, a) },
      { label: 'aDelete', need: AG_EDIT, danger: true, run: deleteConfirm },
    ],
    stats: (c, p): readonly StatVals[] => {
      const s = summaryOf<AgentsSummary>(p, 'by_level');
      if (!s) return [];
      return [
        { label: str(c.t, 'agentCount'), value: String(s.active_agents), sub: levelSummaryText(c.t, s), dark: true },
        { label: str(c.t, 'unsettledTotal'), value: usdt(s.unsettled_total), sub: str(c.t, 'commissions') },
        { label: str(c.t, 'pendingWd'), value: String(s.pending_withdrawals), sub: str(c.t, 'agentWd') },
      ];
    },
    primary: { label: 'newAgent', icon: '+ ', need: AG_EDIT, run: openAgentCreate },
    csvName: 'agents',
  });
}

// 统计卡副标题（多级代理）：顶级人数 · 共 m 级（有在册代理的层数）· 上限 maxLevel
function levelSummaryText(t: Dict, s: AgentsSummary): string {
  const byLevel = Array.isArray(s.by_level) ? s.by_level : [];
  const top = byLevel.find((x) => x.level === 1)?.count ?? 0;
  return interpolate(str(t, 'levelSummary'), { top, levels: byLevel.length, max: s.max_level });
}

// ---- 分销用户（C-10） ----

const agentUsersView = defineView<AgentUserRow>({
  key: 'all',
  source: { route: 'GET /api/admin/agent-users', tabParam: 'tab' },
  echo: '/admin/agent-users',
  cols: [
    { label: 'cUser' }, { label: 'cAgentName' }, { label: 'cLevel', facet: 'level' }, { label: 'cJoined', sortKey: 'created_at' },
    { label: 'cCards' }, { label: 'cContribution', unit: 'USDT' }, { label: 'cEarned', unit: 'USDT' },
  ],
  cells: (c, u) => [
    cell(u.masked_email ?? u.uid, u.uid),
    cell(agentLabel(u.agent)),
    levelChip(u.level),
    cell(fmtDateTime(u.created_at, { tz: tz(c) })),
    cell(String(u.card_count)),
    cell(plain(u.contribution)), // 入金手续费贡献，钱包 USDT（对客评审 #26）
    cell(plain(u.commission_total)),
  ],
  open: (c, u) => openUser360(c, u.id),
  rowActions: () => [{ label: 'aOpen360', run: (c, u) => openUser360(c, u.id) }],
  csvName: 'agent-users',
});

// ---- 佣金结算（C-11） ----

// 06 C-11 计佣基数：open 行 = 1（张，X-07）；topup 行 = 入金手续费（D33）
function baseText(t: Dict, r: CommissionRow): string {
  return r.kind === 'open' ? `${trimDec(r.base_amount)} ${str(t, 'unitCard')}` : usdt(r.base_amount);
}

const beneLabel = (r: CommissionRow): string => r.beneficiary.label;

async function settleOne(ctx: AdminCtx, r: CommissionRow): Promise<void> {
  const alive = sessionGuard(ctx);
  try {
    const res = await ctx.gp.api.call<SettleResult>('POST /api/admin/commissions/{id}/settle', { path: { id: r.id }, body: {} });
    if (!alive()) return;
    ctx.gp.ui.toast(settleText(ctx.t, str(ctx.t, 'toastSettle'), res));
    closeDrawer(ctx);
    await reloadCurrent(ctx);
  } catch (e) {
    if (alive()) ctx.gp.ui.toast(ctx.gp.ui.err(e));
  }
}

// 06 C-11 佣金行抽屉：unsettled → 结算(green)；否则 关闭(primary)
function openCommission(ctx: AdminCtx, r: CommissionRow): void {
  const t = ctx.t;
  const from = r.source.masked_email ?? r.source.uid;
  openDrawer(ctx, {
    kind: str(t, 'commissions'),
    title: `${r.biz_no} · ${usdt(r.amount)}`,
    subtitle: `${beneLabel(r)} ← ${from}`,
    fields: [
      { k: str(t, 'cAgentName'), v: r.beneficiary.kind === 'user' ? `${beneLabel(r)} · ${str(t, 'beneUser')}` : beneLabel(r) },
      { k: str(t, 'cFrom'), v: from },
      { k: str(t, 'cLevel'), v: r.level },
      { k: str(t, 'cType'), v: rowTypeLabel(t, r) },
      { k: str(t, 'cBase'), v: baseText(t, r) },
      { k: str(t, 'cRate'), v: commissionRateText(r.rate_text) },
      { k: str(t, 'cTime'), v: fmtDateTime(r.created_at, { tz: tz(ctx) }) },
      { k: str(t, 'fndSourceRef'), v: r.source_ref ?? '—' },
    ],
    actions:
      r.status === 'unsettled'
        ? [{ label: str(t, 'aSettle'), kind: 'green', need: COM_APPROVE, run: (c) => settleOne(c, r) }]
        : [{ label: str(t, 'aClose'), kind: 'primary', run: (c) => closeDrawer(c) }],
  });
}

function commissionsView(key: 'unsettled' | 'settled' | 'all'): ErasedView {
  return defineView<CommissionRow>({
    key,
    source: { route: 'GET /api/admin/commissions', tabParam: 'tab' },
    echo: '/admin/commissions',
    // sort 仅 created_at（settled_at 未实现，J-10）
    cols: [
      { label: 'cAgentName' }, { label: 'cFrom' }, { label: 'cLevel', facet: 'level' }, { label: 'cType', facet: 'type', facetLabel: typeLabel }, { label: 'cBase' },
      { label: 'cRate' }, { label: 'cAmount', unit: 'USDT' }, { label: 'cTime', sortKey: 'created_at' }, { label: 'cStatus', facet: 'settle_status', facetLabel: settleLabel },
    ],
    cells: (c, r) => {
      const who = cell(beneLabel(r), r.biz_no);
      return [
        r.beneficiary.kind === 'user' ? withBadge(who, 'blue', str(c.t, 'beneUser'), beneLabel(r)) : who,
        cell(r.source.masked_email ?? r.source.uid),
        levelChip(r.level),
        cell(rowTypeLabel(c.t, r), r.entry_kind === 'accrual' ? '' : r.entry_kind),
        cell(baseText(c.t, r)),
        cell(commissionRateText(r.rate_text)), // 旧快照「$0.60」兼容转「0.6 USDT」
        cell(plain(r.amount)),
        cell(fmtDateTime(r.created_at, { tz: tz(c) })),
        r.status === 'settled' ? chip('green', str(c.t, 'stSettled')) : r.status === 'unsettled' ? chip('amber', str(c.t, 'stUnsettled')) : chipOf({}, r.status, 'commission.status'),
      ];
    },
    open: openCommission,
    rowActions: (): readonly ActionDef<CommissionRow>[] => [
      { label: 'aSettle', need: COM_APPROVE, show: (r) => r.status === 'unsettled', run: settleOne },
      { label: 'aDetail', show: (r) => r.status !== 'unsettled', run: openCommission },
    ],
    stats: (c, p): readonly StatVals[] => {
      const s = summaryOf<CommissionsSummary>(p, 'unsettled_total');
      if (!s) return [];
      return [
        { label: str(c.t, 'unsettledTotal'), value: usdt(s.unsettled_total), sub: str(c.t, 'stUnsettled'), dark: true },
        { label: str(c.t, 'settledMonth'), value: usdt(s.month_settled), sub: str(c.t, 'stSettled') },
        { label: str(c.t, 'agentCount'), value: String(s.active_agents), sub: str(c.t, 'agents') },
      ];
    },
    primary: { label: 'aSettle', icon: '+ ', need: COM_APPROVE, run: (c) => settleAllConfirm(c, null) },
    csvName: 'commissions',
  });
}

function beneBadge(t: Dict, n: NegativeRow): CellVals {
  return n.beneficiary_kind === 'user' ? withBadge(cell(n.label), 'blue', str(t, 'beneUser'), n.label) : withBadge(cell(n.label), 'purple', str(t, 'beneAgent'), n.label);
}

const negRed = (v: string): CellVals => {
  const c = cell(plain(v));
  return { ...c, style: `${c.style};color:#d1434b` };
};

// 负余额挂账抽屉：字段同列 + 说明灰条 negTip
function openNegative(ctx: AdminCtx, n: NegativeRow): void {
  const t = ctx.t;
  openDrawer(ctx, {
    kind: str(t, 'commissions'),
    title: n.label,
    subtitle: str(t, 'negTip'),
    fields: [
      { k: str(t, 'fndBeneficiary'), v: `${n.label} · ${str(t, n.beneficiary_kind === 'user' ? 'beneUser' : 'beneAgent')}` },
      { k: str(t, 'cNegSum'), v: usdt(n.negative_total) },
      { k: str(t, 'cNegSince'), v: fmtDate(n.earliest_at, tz(ctx)) },
      { k: str(t, 'cEntries'), v: String(n.entry_count) },
    ],
    actions: [{ label: str(t, 'aClose'), kind: 'primary', run: (c) => closeDrawer(c) }],
  });
}

// 02 §6.2：negative-beneficiaries 只接受 page / size（无 tab / facet / sort / 关键词 / 日期）；服务端固定 earliest_at 升序
const negativeView = defineView<NegativeRow>({
  key: 'negative',
  source: { route: 'GET /api/admin/commissions/negative-beneficiaries', tabParam: 'none' },
  echo: '/admin/commissions/negative-beneficiaries',
  noDate: true,
  noKeyword: true,
  cols: [{ label: 'fndBeneficiary' }, { label: 'cNegSum', unit: 'USDT' }, { label: 'cNegSince' }, { label: 'cEntries' }],
  cells: (c, n) => [beneBadge(c.t, n), negRed(n.negative_total), cell(fmtDate(n.earliest_at, tz(c))), cell(String(n.entry_count))],
  open: openNegative,
  rowActions: () => [{ label: 'aDetail', run: openNegative }],
  csvName: 'commissions-negative',
});

// ---- 提佣审核（C-12） ----

// 手工登记已付哈希 / 重新登记（人工兜底，不触发付款；approved / failed，仅 usdt）：TRON 64 hex；4220 tx_hash（格式 / 已占用）→ errTxHash；1605 由 ui.err 显示服务端 message
function wdPayForm(t: Dict, w: AgentWdRow): FormDef {
  const route: RouteKey = 'POST /api/admin/agent-withdrawals/{id}/mark-paid';
  return {
    title: `${str(t, w.status === 'failed' ? 'aMarkPaidAgain' : 'aPay')} · ${w.biz_no}`,
    tip: str(t, 'markPaidTip').replace('{network}', 'TRC20'),
    fields: [{ k: 'tx_hash', label: str(t, 'cTxHash'), type: 'text', wide: true }],
    init: { tx_hash: '' },
    validate: (v) => (TRON_HASH_RE.test(String(v['tx_hash'] ?? '').trim()) ? '' : str(t, 'errTxHash')),
    submit: async (c, v) => {
      const alive = sessionGuard(c);
      try {
        await c.gp.api.call(route, { path: { id: w.id }, body: { tx_hash: String(v['tx_hash']).trim() } });
      } catch (e) {
        if (e instanceof ApiError && e.code === 4220 && e.data.field === 'tx_hash') throw localErr(route, str(t, 'errTxHash'));
        throw e;
      }
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastMarkPaid'));
      closeDrawer(c);
      void reloadCurrent(c);
    },
  };
}

function wdRejectForm(t: Dict, w: AgentWdRow): FormDef {
  return {
    title: `${str(t, 'aReject')} · ${w.biz_no}`,
    fields: [{ k: 'note', label: str(t, 'reviewNote'), type: 'text', wide: true, optional: true, placeholder: str(t, 'notePh') }],
    init: { note: '' },
    validate: (v) => (String(v['note'] ?? '').trim().length <= NOTE_MAX ? '' : str(t, 'errNoteLong')),
    submit: async (c, v) => {
      const alive = sessionGuard(c);
      await c.gp.api.call('POST /api/admin/agent-withdrawals/{id}/reject', { path: { id: w.id }, body: { note: String(v['note'] ?? '').trim() } });
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastWdReject'));
      c.events.emit('profile', {});
      closeDrawer(c);
      void reloadCurrent(c);
    },
  };
}

async function wdReject(ctx: AdminCtx, w: AgentWdRow, note: string): Promise<void> {
  if (note.length > NOTE_MAX) {
    ctx.gp.ui.toast(str(ctx.t, 'errNoteLong'));
    return;
  }
  const alive = sessionGuard(ctx);
  try {
    await ctx.gp.api.call('POST /api/admin/agent-withdrawals/{id}/reject', { path: { id: w.id }, body: { note } });
    if (!alive()) return;
    ctx.gp.ui.toast(str(ctx.t, 'toastWdReject'));
    ctx.events.emit('profile', {});
    closeDrawer(ctx);
    await reloadCurrent(ctx);
  } catch (e) {
    if (alive()) ctx.gp.ui.toast(ctx.gp.ui.err(e));
  }
}

// 通过：wallet 方式审核即入账（status=paid，X-03）→ toastWdPaid；usdt 方式进入待支付（approved）→ toastWdApproved
async function wdApprove(ctx: AdminCtx, w: AgentWdRow, note = ''): Promise<void> {
  const alive = sessionGuard(ctx);
  try {
    const r = await ctx.gp.api.call<{ id: number; status: string }>('POST /api/admin/agent-withdrawals/{id}/approve', { path: { id: w.id }, body: note ? { note } : {} });
    if (!alive()) return;
    ctx.gp.ui.toast(str(ctx.t, r.status === 'paid' ? 'toastWdPaid' : 'toastWdApproved'));
    ctx.events.emit('profile', {});
    closeDrawer(ctx);
    await reloadCurrent(ctx);
  } catch (e) {
    if (alive()) ctx.gp.ui.toast(ctx.gp.ui.err(e));
  }
}

const canReject = (w: AgentWdRow): boolean => w.status === 'pending' || w.status === 'approved' || w.status === 'failed';
const canPay = (w: AgentWdRow): boolean => w.method === 'usdt' && w.status === 'approved';
const canRepay = (w: AgentWdRow): boolean => w.method === 'usdt' && w.status === 'failed';
const methodText = (t: Dict, w: AgentWdRow): string => str(t, w.method === 'usdt' ? 'mUsdt' : 'mUsd');
const methodSub = (w: AgentWdRow): string => (w.method === 'usdt' ? short(w.usdt_address) : w.target_uid ? `→ ${w.target_uid}` : '');

// 06 C-12 抽屉：底部按钮与提币对称（可拒绝态 {pending, approved, failed}，X-02）
function openAgentWd(ctx: AdminCtx, w: AgentWdRow): void {
  const t = ctx.t;
  const note = (c: AdminCtx): string => c.host.state.tb.note.trim();
  const actions: DrawerAction[] = [];
  if (canReject(w)) actions.push({ label: str(t, 'aReject'), kind: 'danger', need: AW_APPROVE, run: (c) => wdReject(c, w, note(c)) });
  if (w.status === 'pending') actions.push({ label: str(t, 'aApprove'), kind: 'green', need: AW_APPROVE, run: (c) => wdApprove(c, w, note(c)) });
  if (canPay(w) || canRepay(w)) actions.push({ label: str(t, canPay(w) ? 'aPay' : 'aMarkPaidAgain'), kind: 'green', need: AW_APPROVE, run: (c) => openForm(c, wdPayForm(t, w)) });
  if (actions.length === 0) actions.push({ label: str(t, 'aClose'), kind: 'primary', run: (c) => closeDrawer(c) });
  const showHash = w.status === 'paying' || w.status === 'paid' || w.status === 'failed';
  openDrawer(ctx, {
    kind: str(t, 'agentWd'),
    title: `${w.biz_no} · ${usdt(w.amount)}`,
    subtitle: w.status === 'failed' ? `${agentLabel(w.agent)} · ${str(t, 'agentWdFailedTip')}` : agentLabel(w.agent),
    fields: [
      { k: str(t, 'cAgentName'), v: agentLabel(w.agent) },
      { k: str(t, 'cAmount'), v: usdt(w.amount) },
      { k: str(t, 'cFee'), v: usdt(w.fee) },
      { k: str(t, 'cNetAmount'), v: usdt(w.net_amount) },
      { k: str(t, 'cMethod'), v: `${methodText(t, w)}${methodSub(w) ? ` · ${methodSub(w)}` : ''}` },
      { k: str(t, 'cAvail'), v: usdt(w.available) },
      { k: str(t, 'cApplied'), v: fmtDateTime(w.created_at, { tz: tz(ctx) }) },
      { k: str(t, 'fndApprovedAt'), v: w.approved_at ? fmtDateTime(w.approved_at, { tz: tz(ctx) }) : '—' },
      { k: str(t, 'cStatus'), v: chipOf(chipMap(t, WD_STATUS), w.status, 'agent_wd.status').v },
      { k: 'Address', v: w.usdt_address ?? '—', wide: true },
      ...(showHash ? [{ k: str(t, 'cTxHash'), v: w.tx_hash ?? '—', wide: true }] : []),
    ],
    note: w.status !== 'paid' && w.status !== 'rejected',
    actions,
  });
}

const WD_TABS = ['pending', 'approved', 'paying', 'paid', 'failed', 'rejected', 'all'] as const;

function agentWdView(key: (typeof WD_TABS)[number]): ErasedView {
  return defineView<AgentWdRow>({
    key,
    source: { route: 'GET /api/admin/agent-withdrawals', tabParam: 'tab' },
    echo: '/admin/agent-withdrawals',
    // 状态筛选由 7 页签承担，不设 status facet（?status= → 4220，GAP-37）；sort 仅 created_at（paid_at 未实现，J-10）
    cols: [
      { label: 'cId' }, { label: 'cAgentName' }, { label: 'cAmount', unit: 'USDT' }, { label: 'cFee', unit: 'USDT' }, { label: 'cNetAmount', unit: 'USDT' }, { label: 'cMethod' },
      { label: 'cAvail', unit: 'USDT' }, { label: 'cApplied', sortKey: 'created_at' }, { label: 'cStatus' },
    ],
    cells: (c, w) => [
      cell(w.biz_no, '', true),
      cell(agentLabel(w.agent)),
      cell(plain(w.amount)),
      cell(plain(w.fee)),
      cell(plain(w.net_amount)),
      cell(methodText(c.t, w), methodSub(w)),
      cell(plain(w.available)),
      cell(fmtDateTime(w.created_at, { tz: tz(c) })),
      // 06 C-12：approved 超 SLA 满 1 小时时状态章下方红字「超时 {h}h」（D26，sla_overdue_hours 未超时为 null）
      slaChip(c.t, chipOf(chipMap(c.t, WD_STATUS), w.status, 'agent_wd.status'), w.sla_overdue_hours),
    ],
    open: openAgentWd,
    rowActions: (c): readonly ActionDef<AgentWdRow>[] => [
      { label: 'aReject', need: AW_APPROVE, danger: true, show: canReject, run: (cc, w) => openForm(cc, wdRejectForm(c.t, w)) },
      { label: 'aApprove', need: AW_APPROVE, show: (w) => w.status === 'pending', run: (cc, w) => wdApprove(cc, w) },
      { label: 'aPay', need: AW_APPROVE, show: canPay, run: (cc, w) => openForm(cc, wdPayForm(c.t, w)) },
      { label: 'aMarkPaidAgain', need: AW_APPROVE, show: canRepay, run: (cc, w) => openForm(cc, wdPayForm(c.t, w)) },
      { label: 'aDetail', show: (w) => w.status === 'paying' || w.status === 'paid' || w.status === 'rejected', run: openAgentWd },
    ],
    stats: (c, p): readonly StatVals[] => {
      const s = summaryOf<AgentWdSummary>(p, 'pending_count');
      if (!s) return [];
      return [
        { label: str(c.t, 'pendingWd'), value: String(s.pending_count), sub: usdt(s.pending_total), dark: true },
        { label: str(c.t, 'toPayAmount'), value: usdt(s.approved_total), sub: String(s.approved_count) },
        { label: str(c.t, 'paidMonth'), value: usdt(s.month_paid), sub: str(c.t, 'stPaid') },
      ];
    },
    csvName: 'agent-withdrawals',
  });
}

const PACK = { i18n: I18N_AGENTS, rewrites: REWRITES_AGENTS } as const;

export const PAGES_AGENTS: PageMap = {
  agents: { page: 'agents', ...PACK, tabLabels: (t) => strArr(t, 'tabAgentsAll'), views: [agentsView('all')] },
  agentUsers: { page: 'agentUsers', ...PACK, tabLabels: (t) => [str(t, 'all')], views: [agentUsersView] },
  commissions: {
    page: 'commissions',
    ...PACK,
    tabLabels: (t) => [...strArr(t, 'tabCommissions'), str(t, 'tabNegative')],
    views: [commissionsView('unsettled'), commissionsView('settled'), commissionsView('all'), negativeView],
  },
  agentWd: { page: 'agentWd', ...PACK, tabLabels: (t) => strArr(t, 'tabAgentWd'), views: WD_TABS.map(agentWdView) },
};
